import React from "react";
import { useSelector } from "react-redux";
import { makeStyles, createStyles, useTheme } from "@material-ui/core/styles";
import PropTypes from "prop-types";
import { EditableInputMemoized } from "./EditableInput";
import SoundBar from "../../Common/SoundBar";
import { green, yellow, orange, red } from "@material-ui/core/colors";
import Chip from "@material-ui/core/Chip";
import SignalCellularOffIcon from "@material-ui/icons/SignalWifiOff";
import SignalCellular0BarIcon from "@material-ui/icons/SignalWifi0Bar";
import SignalCellular1BarIcon from "@material-ui/icons/SignalWifi1Bar";
import SignalCellular2BarIcon from "@material-ui/icons/SignalWifi2Bar";
import SignalCellular3BarIcon from "@material-ui/icons/SignalWifi3Bar";
import SignalCellular4BarIcon from "@material-ui/icons/SignalWifi4Bar";
import ScreenIcon from "@material-ui/icons/AirplayOutlined";
import MicOffIcon from "@material-ui/icons/MicOffOutlined";
import useMediaQuery from "@material-ui/core/useMediaQuery/useMediaQuery";

const useStyles = makeStyles((theme) =>
  createStyles({
    peerInfoLayer: {
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      zIndex: 10,
      position: "absolute",
      // padding: theme.spacing(2),
      // [theme.breakpoints.down("xs")]: {
      //   padding: theme.spacing(0),
      // },
    },
    miniPeerInfoLayer: {
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      zIndex: 10,
      position: "absolute",
      padding: theme.spacing(0),
    },
    bottomBar: {
      display: "flex",
      justifyContent: "space-between",
    },
    topBar: {
      display: "flex",
      justifyContent: "flex-end",
    },
    micOffIcon: {
      color: "#fff",
      backgroundColor: "#e53935",
      padding: 4,
      borderRadius: "50%",
    },
    qualityIcon: {},
    media: {
      display: "flex",
      transitionProperty: "opacity",
      transitionDuration: ".15s",
      "&.hidden": {
        opacity: 0,
        transitionDuration: "0s",
      },
      color: "#ffffff",
      backgroundColor: "rgba(0, 0, 0, 0.3)",
      "-webkit-backdrop-filter": "blur(8px)",
      backdropFilter: "blur(8px)",
      borderRadius: "50%",
      padding: theme.spacing(1),
      [theme.breakpoints.down("xs")]: {
        width: 24,
        height: 24,
        padding: theme.spacing(0.37),
      },
    },
    box: {
      color: "#ffffff",
      backgroundColor: "rgba(0, 0, 0, 0.3)",
      "-webkit-backdrop-filter": "blur(8px)",
      backdropFilter: "blur(8px)",
      "& .MuiChip-icon": {
        marginRight: 0,
      },
      maxWidth: "100%",
      marginLeft: 10,
    },
    displayNameEdit: {
      fontSize: 14,
      fontWeight: 500,
      color: "rgba(255, 255, 255, 1)",
      border: "none",
      marginRight: theme.spacing(1),
      borderBottom: "1px solid #0A8AFF",
      backgroundColor: "transparent",
      [theme.breakpoints.down("xs")]: {
        fontSize: 13,
      },
    },
    displayNameStatic: {
      userSelect: "none",
      cursor: "text",
      fontSize: 16,
      fontWeight: 500,
      color: "rgba(255, 255, 255, 1)",
      "&:hover": {
        backgroundColor: "rgba(10,138,255,0.87)",
      },
      [theme.breakpoints.down("xs")]: {
        fontSize: 13,
      },
    },
  })
);

const PeerInfo = ({
  audioScore,
  videoScore,
  showPeerInfo,
  micOff,
  isMe,
  displayName,
  peer,
  mini = false,
  isScreen,
  onFilmStrip = false,
  onClick,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("xs"));
  const roomMode = useSelector((state) => state.room.mode);

  let quality = (
    <SignalCellularOffIcon style={{ fontSize: 18, color: red[500] }} />
  );

  if (videoScore || audioScore) {
    const score = videoScore ? videoScore : audioScore;
    switch (score.producerScore) {
      case 0: {
        quality = (
          <SignalCellular0BarIcon style={{ fontSize: 18, color: red[500] }} />
        );
        break;
      }
      case 1: {
        quality = (
          <SignalCellular1BarIcon style={{ fontSize: 18, color: red[500] }} />
        );
        break;
      }
      case 2: {
        quality = (
          <SignalCellular2BarIcon
            style={{ fontSize: 18, color: orange[500] }}
          />
        );
        break;
      }
      case 3: {
        quality = (
          <SignalCellular3BarIcon
            style={{ fontSize: 18, color: yellow[500] }}
          />
        );
        break;
      }
      case 4: {
        quality = (
          <SignalCellular4BarIcon style={{ fontSize: 18, color: green[500] }} />
        );
        break;
      }
      default: {
        break;
      }
    }
  }

  return (
    <div
      onClick={() => {
        if (onClick) onClick();
      }}
      className={mini ? classes.miniPeerInfoLayer : classes.peerInfoLayer}
    >
      <div className={classes.topBar}>
        {/* {(audioScore || videoScore) && (
          <div className={classes.media}>{quality}</div>
        )} */}
      </div>
      <div className={classes.bottomBar}>
        {showPeerInfo && (
          <Chip
            size={roomMode === "filmstrip" || isXs || mini ? "small" : "medium"}
            className={classes.box}
            color="primary"
            icon={
              isScreen ? (
                <ScreenIcon />
              ) : micOff ? (
                <MicOffIcon className={classes.micOffIcon} />
              ) : peer ? (
                <SoundBar id={peer.id} />
              ) : null
            }
            label={
              isMe ? (
                <EditableInputMemoized
                  value={displayName}
                  propName="newDisplayName"
                  className={classes.displayNameEdit}
                  classLoading="loading"
                  classInvalid="invalid"
                  shouldBlockWhileLoading
                />
              ) : (
                <span className={classes.displayNameStatic}>{displayName}</span>
              )
            }
          />
        )}
      </div>
    </div>
  );
};

PeerInfo.propTypes = {
  isMe: PropTypes.bool,
  isScreen: PropTypes.bool,
  displayName: PropTypes.string,
  showPeerInfo: PropTypes.bool,
  micOff: PropTypes.bool,
  audioScore: PropTypes.any,
  videoScore: PropTypes.any,
  peer: PropTypes.object,
  onFilmStrip: PropTypes.bool,
  mini: PropTypes.bool,
};

export default PeerInfo;
